import { useMemo } from "react";

import { Box, Collapse, Stack } from "@mantine/core";

import { BenchmarkingToolsNavigation } from "lib/benchmarking/components/BenchmarkingToolsNavigation";
import { DatasetNavigation } from "lib/benchmarking/components/DatasetNavigation";
import { useEcsUserType } from "lib/frontend/cm/pages/internal-tools/ecs/_hooks/useEcsUserType";
import { EcsUserType } from "lib/shared/users/getEcsUserType";

interface BenchmarkingSidebarProps {
  activeDataset?: "comprehensive" | "mercer" | "comptryx" | "salary" | "carta";
  topSection?: React.ReactNode;
}

/**
 * Unified sidebar component for benchmarking pages
 * Combines DatasetNavigation and BenchmarkingToolsNavigation with consistent styling
 */
export function BenchmarkingSidebar({ activeDataset, topSection }: BenchmarkingSidebarProps) {
  const memoizedTopSection = useMemo(() => topSection, [topSection]);
  const ecsUserType = useEcsUserType();

  const isVcCompany = ecsUserType === EcsUserType.VC;
  const isNonCmEcsParticipant = ecsUserType === EcsUserType.NON_CM_ECS_PARTICIPANT;
  const isShareLinkRecipient = ecsUserType === EcsUserType.SHARE_LINK_RECIPIENT;

  const isGated = useMemo(() => {
    return isShareLinkRecipient;
  }, [isShareLinkRecipient]);

  return (
    <Box
      style={{
        borderRight: "1px solid var(--mantine-color-gray-3)",
        overflowY: "auto",
        backgroundColor: "var(--mantine-color-gray-1)",
        overflow: "hidden",
      }}
      w={300}
      display={{ base: "none", lg: "block" }}
      sx={{ borderRight: "1px solid #D0D5DD" }}
    >
      <Stack px="md" gap="lg">
        <Collapse
          in={!!memoizedTopSection}
          transitionDuration={1000}
          transitionTimingFunction="linear"
        >
          {memoizedTopSection}
        </Collapse>
        <DatasetNavigation activeDataset={activeDataset} isGated={isGated} />
        {!isVcCompany && !isShareLinkRecipient && (
          <BenchmarkingToolsNavigation isGated={isNonCmEcsParticipant} />
        )}
      </Stack>
    </Box>
  );
}
